import { useAuth } from "@clerk/clerk-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CircularProgress from "@mui/material/CircularProgress";
import { type FormEvent, type KeyboardEvent, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthAltLink, AuthCard, AuthFloatingField } from "@/components/auth";
import { useSignInFlow } from "@/hooks/useSignInFlow";
import { PAGE_VIEW_STATE } from "@/lib/analytics";

/**
 * SignInPage — custom passwordless (OTP) sign-in matching the designer's demo.
 *
 * Step 1: user enters email → "Continue" sends a one-time code.
 * Step 2: code field revealed, user enters OTP → "Continue" verifies & signs in.
 *
 * Replaces the previous 10-line Clerk <SignIn /> drop-in component.
 */
export default function SignInPage() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const {
    step,
    email,
    code,
    isLoading,
    error,
    isReady,
    setEmail,
    setCode,
    sendCode,
    verifyCode,
    resendCode,
    goBack,
  } = useSignInFlow();

  // If already signed in, redirect to home.
  if (isSignedIn) {
    return <Navigate to="/" replace />;
  }

  // Wait for Clerk to load.
  if (!isReady) {
    return (
      <main className="gfg-auth-screen">
        <CircularProgress aria-label="Signing in" sx={{ color: "var(--color-dark-dust)" }} />
      </main>
    );
  }

  const handleContinue = async (e: FormEvent) => {
    e.preventDefault();
    if (step === "email") {
      await sendCode();
      // Focus the code input after transition.
      setTimeout(() => codeInputRef.current?.focus(), 100);
    } else {
      const success = await verifyCode();
      if (success) {
        navigate("/", { replace: true, state: PAGE_VIEW_STATE });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleContinue(e as unknown as FormEvent);
    }
  };

  const handleResend = async () => {
    await resendCode();
  };

  return (
    <main className="gfg-auth-screen">
      <AuthCard>
        {step === "verifying" && (
          <button type="button" className="gfg-auth-back" onClick={goBack}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back
          </button>
        )}

        <h1 className="gfg-auth-title">Sign in</h1>
        <p className="gfg-auth-sub">Welcome back to Gifts for Good.</p>

        <form onSubmit={handleContinue}>
          <AuthFloatingField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={step === "verifying"}
            autoFocus={step === "email"}
          />

          {step === "verifying" && (
            <>
              <AuthFloatingField
                ref={codeInputRef}
                label="Verification code"
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <p className="gfg-auth-hint gfg-auth-hint-strong">
                We sent a code to your inbox. If you don't see it within 5 minutes, check your spam
                folder.
              </p>
            </>
          )}

          {error && (
            <p className="gfg-auth-hint" style={{ color: "var(--color-good-wine)", marginTop: 8 }}>
              {error}
            </p>
          )}

          <button type="submit" className="gfg-auth-btn" disabled={isLoading}>
            {isLoading ? (
              <CircularProgress
                aria-label="Signing in"
                size={18}
                sx={{ color: "var(--color-white)" }}
              />
            ) : (
              "Continue"
            )}
          </button>
        </form>

        {step === "verifying" && (
          <div className="gfg-auth-alt">
            <button type="button" onClick={handleResend} disabled={isLoading}>
              Resend verification code
            </button>
          </div>
        )}

        {step === "email" && (
          <AuthAltLink text="Don't have an account?" linkText="Sign up" to="/sign-up" />
        )}
      </AuthCard>
    </main>
  );
}
